import { useEffect, useState } from 'react';
import { Search, FileText, Brain } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { adminReportsApi, promptExamplesApi, type AdminReport } from '../../lib/reportTemplatesApi';
import ReportPreviewModal from '../ReportPreviewModal';
import Alert from './Alert';
import ConfirmDialog from './ConfirmDialog';
import type { AdminUser } from './UsersTab';

interface Props {
  users: AdminUser[];
}

const STATUS_OPTIONS = ['Neu', 'In Bearbeitung', 'Generiert', 'Geprüft', 'Abgeschlossen'];

export default function BerichteTab({ users }: Props) {
  const { darkMode } = useDarkMode();
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterUserId, setFilterUserId] = useState<number | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [savedReportIds, setSavedReportIds] = useState<Set<number>>(new Set());

  const loadSavedReportIds = async () => {
    try {
      const ids = await promptExamplesApi.savedReportIds();
      setSavedReportIds(new Set(ids));
    } catch { /* ignore */ }
  };

  const handleSaveAsExample = async (reportId: number) => {
    setSavingId(reportId);
    try {
      const result = await promptExamplesApi.saveFromReport(reportId);
      setSavedMsg(`${result.saved} Abschnitte gespeichert`);
      setSavedReportIds(prev => new Set([...prev, reportId]));
      setTimeout(() => setSavedMsg(null), 3000);
    } catch {
      setSavedMsg('Fehler beim Speichern');
      setTimeout(() => setSavedMsg(null), 3000);
    } finally {
      setSavingId(null);
    }
  };

  const load = async (overrides?: { search?: string; userId?: number; status?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const filters: { search?: string; userId?: number; status?: string } = {};
      const s = overrides && 'search' in overrides ? overrides.search : search;
      const u = overrides && 'userId' in overrides ? overrides.userId : filterUserId;
      const st = overrides && 'status' in overrides ? overrides.status : filterStatus;
      if (s) filters.search = s;
      if (u !== undefined) filters.userId = u;
      if (st) filters.status = st;
      const data = await adminReportsApi.list(filters);
      setReports(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    void loadSavedReportIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    void load({ search: value || undefined });
  };

  const handleUserChange = (raw: string) => {
    const v = raw === '' ? undefined : Number(raw);
    setFilterUserId(v);
    void load({ userId: v });
  };

  const handleStatusChange = (raw: string) => {
    const v = raw === '' ? undefined : raw;
    setFilterStatus(v);
    void load({ status: v });
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const inputCls = darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-3 flex flex-wrap items-center gap-2 ${card}`}>
        <div className="flex-1 min-w-[180px] flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Suche (Titel, Kunde, User-Email…)"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <label htmlFor="filter-user" className="text-xs sr-only">User</label>
        <select
          id="filter-user"
          aria-label="User"
          value={filterUserId ?? ''}
          onChange={(e) => handleUserChange(e.target.value)}
          className={`text-sm px-2 py-1 border rounded ${inputCls}`}
        >
          <option value="">Alle User</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.companyName || u.contactPerson} ({u.email})
            </option>
          ))}
        </select>
        <label htmlFor="filter-status" className="text-xs sr-only">Status</label>
        <select
          id="filter-status"
          aria-label="Status"
          value={filterStatus ?? ''}
          onChange={(e) => handleStatusChange(e.target.value)}
          className={`text-sm px-2 py-1 border rounded ${inputCls}`}
        >
          <option value="">Alle Status</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {savedMsg && (
        <Alert type={savedMsg.startsWith('Fehler') ? 'error' : 'success'} className="mb-3">
          KI-Lernbeispiel: {savedMsg}
        </Alert>
      )}

      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full min-w-[640px] text-sm table-fixed">
          <colgroup>
            <col className="w-[38%]" />
            <col className="w-[24%]" />
            <col className="w-[24%]" />
            <col className="w-[14%]" />
          </colgroup>
          <thead>
            <tr className={`border-b ${darkMode ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
              <th className="px-4 py-2 text-left">Titel</th>
              <th className="px-4 py-2 text-left">Kunde</th>
              <th className="px-4 py-2 text-left">User</th>
              <th className="px-4 py-2 text-center">Aktualisiert</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
            {loading ? (
              <tr><td colSpan={4} className={`px-4 py-8 text-center ${muted}`}>Lädt…</td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan={4} className={`px-4 py-8 text-center ${muted}`}>Keine Berichte gefunden</td></tr>
            ) : (
              reports.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setPreviewId(r.id)}
                  className={`cursor-pointer transition-colors ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'} ${r.deletedAt ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-2 font-medium max-w-0">
                    <span className="flex items-center gap-1.5">
                      <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="truncate flex-1">{r.berichtstitel || '–'}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmId(r.id); }}
                        disabled={savingId === r.id}
                        title={savedReportIds.has(r.id) ? 'Bereits als KI-Lernbeispiel gespeichert' : 'Als KI-Lernbeispiel speichern'}
                        className={`p-1 rounded flex-shrink-0 transition-colors disabled:opacity-40 ${
                          savedReportIds.has(r.id)
                            ? darkMode ? 'text-green-400 hover:bg-slate-600' : 'text-green-600 hover:bg-slate-100'
                            : darkMode ? 'hover:bg-slate-600 text-slate-500 hover:text-purple-400' : 'hover:bg-slate-100 text-slate-400 hover:text-purple-600'
                        }`}
                      >
                        {savingId === r.id ? <span className="text-xs">…</span> : <Brain className="w-3.5 h-3.5" />}
                      </button>
                    </span>
                  </td>
                  <td className="px-4 py-2 truncate max-w-0">{r.customer?.companyName || '–'}</td>
                  <td className="px-4 py-2 max-w-0">
                    <div className="text-xs truncate">{r.user?.companyName || r.user?.contactPerson}</div>
                    <div className={`text-xs truncate ${muted}`}>{r.user?.email}</div>
                  </td>
                  <td className={`px-4 py-2 text-center text-xs ${muted}`}>
                    {new Date(r.updatedAt).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <p className={`text-xs ${muted}`}>{reports.length} Berichte · Klick auf Zeile für Vorschau</p>

      {previewId !== null && (
        <ReportPreviewModal
          reportId={previewId}
          open={previewId !== null}
          onClose={() => setPreviewId(null)}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title={confirmId !== null && savedReportIds.has(confirmId) ? 'Bereits gespeichert — überschreiben?' : 'Als KI-Lernbeispiel speichern?'}
        message={confirmId !== null && savedReportIds.has(confirmId)
          ? 'Dieser Bericht wurde bereits als KI-Lernbeispiel gespeichert. Wenn du fortfährst, werden die alten Einträge gelöscht und durch neue ersetzt.'
          : 'Alle Abschnitte dieses Berichts werden in die Lernbibliothek übernommen und zukünftig bei der Generierung ähnlicher Branchen verwendet.'}
        confirmLabel={confirmId !== null && savedReportIds.has(confirmId) ? 'Überschreiben' : 'Speichern'}
        onConfirm={() => { if (confirmId !== null) { handleSaveAsExample(confirmId); } setConfirmId(null); }}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}
